import { c as _c } from "react-compiler-runtime";
import { useCallback, useMemo, memo } from 'react';
import { Checkbox } from 'foundations-components/transitional/components/Checkbox';
import { useSelectionDispatch, useSelectionSelector } from '../../redux/reduxHooks';
import { makeGetIsRowSelected, getIsQuerySelected, cellClicked } from '../redux/selectionSlice';
import { useIsInfiniteScrollEnabled } from '../../table/context/InfiniteScrollContext';
import I18n from 'I18n';
import { Flex } from 'foundations-components/components/Flex';

/**
 * Renders the row selection checkbox in a table's selection column.
 *
 * ## Accessible name strategy (aria-labelledby)
 *
 * Without special handling, JAWS reads this checkbox as just
 * "Select row, checkbox" — with no indication of which row. To fix this,
 * we use `aria-labelledby` to combine two text sources into the
 * checkbox's accessible name:
 *
 *   1. A hidden <span> containing "Select row" (the i18n label)
 *   2. The first column's content wrapper (e.g. the contact name)
 *
 * This makes JAWS announce: "Select row John Doe, unchecked, checkbox"
 *
 * The `firstCellContentId` prop is the DOM id of the first column's
 * content wrapper — specifically CSSHoverCell's ChildrenWrapper, which
 * contains only the primary display text (excluding hover actions like
 * "Preview"). It's computed by Row.tsx using `getCellContentId()` from
 * cellIdUtils.ts and passed down.
 *
 * When `firstCellContentId` is undefined (e.g. empty columns array),
 * the checkbox falls back to just "Select row".
 *
 * @see cellIdUtils.ts — getCellContentId / toContentId
 * @see CSSHoverCell.tsx — ChildrenWrapper with toContentId(id)
 * @see Row.tsx — computes and passes firstCellContentId
 * @see Row.test.tsx — "includes the first cell content in the checkbox accessible name"
 */
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const SelectRowCheckboxCell = t0 => {
  const $ = _c(18);
  const {
    rowId,
    rowIndex,
    firstCellContentId
  } = t0;
  const isInfiniteScroll = useIsInfiniteScrollEnabled();
  let t1;
  if ($[0] !== rowId) {
    t1 = makeGetIsRowSelected(rowId);
    $[0] = rowId;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const getIsRowSelected = t1;
  const isRowInSelection = useSelectionSelector(getIsRowSelected);
  const isQuerySelected = useSelectionSelector(getIsQuerySelected);
  const isSelected = isInfiniteScroll && isQuerySelected ? true : isRowInSelection;
  const dispatch = useSelectionDispatch();
  let t2;
  if ($[2] !== dispatch || $[3] !== isInfiniteScroll || $[4] !== rowId || $[5] !== rowIndex) {
    t2 = event => {
      const {
        checked
      } = event.target;
      const isShiftClick = event.nativeEvent instanceof MouseEvent && event.nativeEvent.shiftKey;
      dispatch(cellClicked({
        rowIndex,
        rowId,
        checked,
        isShiftClick,
        isInfiniteScroll
      }));
    };
    $[2] = dispatch;
    $[3] = isInfiniteScroll;
    $[4] = rowId;
    $[5] = rowIndex;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const handleChange = t2;
  const selectLabelId = `select-row-label-${rowId}`;
  const labelledBy = firstCellContentId ? `${selectLabelId} ${firstCellContentId}` : selectLabelId;
  let t3;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = I18n.text("datatable.selectRowCheckbox.ariaLabel");
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== selectLabelId) {
    t4 = /*#__PURE__*/_jsx("span", {
      id: selectLabelId,
      hidden: true,
      children: t3
    });
    $[8] = selectLabelId;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const t5 = `checkbox-select-row-${rowId}`;
  let t6;
  if ($[10] !== handleChange || $[11] !== isSelected || $[12] !== labelledBy || $[13] !== t5) {
    t6 = /*#__PURE__*/_jsx(Flex, {
      alignItems: "center",
      justifyContent: "center",
      blockSize: "100%",
      inlineSize: "100%",
      children: /*#__PURE__*/_jsx(Checkbox, {
        "aria-labelledby": labelledBy,
        "data-test-id": t5,
        checked: isSelected,
        onChange: handleChange
      })
    });
    $[10] = handleChange;
    $[11] = isSelected;
    $[12] = labelledBy;
    $[13] = t5;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  if ($[15] !== t4 || $[16] !== t6) {
    t7 = /*#__PURE__*/_jsxs("td", {
      "data-selection-column": true,
      children: [t4, t6]
    });
    $[15] = t4;
    $[16] = t6;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  return t7;
};
export default /*#__PURE__*/memo(SelectRowCheckboxCell);